import type { AdminOrderDetail, AdminOrderRow, OrderStatusFilter, SalesRepRef } from './types';

interface ListParams {
  sales_rep_ids?: string[];
  search?: string;
  status?: OrderStatusFilter[];
  date_from?: string;
  date_to?: string;
  page?: number;
  pageSize?: number;
}

export async function fetchAdminOrders(
  params: ListParams,
): Promise<{ rows: AdminOrderRow[]; total: number }> {
  const url = new URL('/api/admin/orders', window.location.origin);
  for (const id of params.sales_rep_ids ?? []) url.searchParams.append('sales_rep_id', id);
  for (const s of params.status ?? []) url.searchParams.append('status', s);
  if (params.search) url.searchParams.set('search', params.search);
  if (params.date_from) url.searchParams.set('date_from', params.date_from);
  if (params.date_to) url.searchParams.set('date_to', params.date_to);
  if (params.page) url.searchParams.set('page', String(params.page));
  if (params.pageSize) url.searchParams.set('page_size', String(params.pageSize));
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`Hentning af ordrer fejlede (${res.status})`);
  return (await res.json()) as { rows: AdminOrderRow[]; total: number };
}

export async function fetchAdminOrder(id: string): Promise<AdminOrderDetail> {
  const res = await fetch(`/api/admin/orders/${id}`);
  if (!res.ok) throw new Error(`Hentning af ordre fejlede (${res.status})`);
  const json = (await res.json()) as { order: AdminOrderDetail };
  return json.order;
}

export async function fetchOrdersSalesReps(): Promise<SalesRepRef[]> {
  const res = await fetch('/api/admin/orders/_sales-reps');
  if (!res.ok) return [];
  const json = (await res.json()) as { reps: SalesRepRef[] };
  return json.reps;
}

export async function patchAdminOrder(
  id: string,
  patch: Partial<{
    orderer_name: string | null;
    orderer_email: string | null;
    orderer_phone: string | null;
    order_summary_md: string | null;
    pipeline_clickup_task_id: string | null;
    add_on_cross_sales_notes: string | null;
    client_marketing_intel: string | null;
    extra_sales_rep_id: string | null;
    extra_sales_rep_share_pct: 25 | 50 | 75 | null;
  }>,
): Promise<AdminOrderDetail> {
  const res = await fetch(`/api/admin/orders/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Opdatering fejlede (${res.status}): ${body}`);
  }
  const json = (await res.json()) as { order: AdminOrderDetail };
  return json.order;
}

export async function voidAdminOrder(id: string, reason: string): Promise<AdminOrderDetail> {
  const res = await fetch(`/api/admin/orders/${id}/void`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ void_reason: reason }),
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Annullering fejlede (${res.status}): ${body}`);
  }
  const json = (await res.json()) as { order: AdminOrderDetail };
  return json.order;
}

export interface ContractUploadInit {
  upload_url: string;
  r2_key: string;
  expires_at: string;
}

export async function initContractUpload(
  filename: string,
  contentType: string,
): Promise<ContractUploadInit> {
  const res = await fetch('/api/intake/contract-upload', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filename, content_type: contentType }),
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Kunne ikke starte upload (${res.status}): ${body}`);
  }
  return (await res.json()) as ContractUploadInit;
}

export async function uploadContract(uploadUrl: string, file: File): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Upload til R2 fejlede (${res.status}): ${body}`);
  }
}

export async function attachContractToOrder(orderId: string, r2Key: string): Promise<void> {
  // Re-uses the patch endpoint but with a body the server treats as
  // setting `signed_contract_r2_key`. The PATCH endpoint deliberately
  // does NOT accept the R2 key on the edit form (immutable except via
  // upload), so this endpoint posts to a small dedicated handler.
  const res = await fetch(`/api/admin/orders/${orderId}/attach-contract`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ r2_key: r2Key }),
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Kontraktbinding fejlede (${res.status}): ${body}`);
  }
}

export function downloadContractUrl(orderId: string): string {
  return `/api/admin/orders/${orderId}/contract-download`;
}
